// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import {
  isVideoTransformDevice,
  VideoInputDevice,
  VideoTransformDevice,
} from "amazon-chime-sdk-js";
import isEqual from "lodash.isequal";
import React, { ReactNode, useEffect, useState } from "react";
import {
  useBackgroundBlur,
  useBackgroundReplacement,
  useVideoInputs,
  useLocalVideo,
  useMeetingManager,
  isOptionActive,
  useLogger,
} from "amazon-chime-sdk-component-library-react";
import { ButtonDropdownCombo } from "@themes/pollinator/ui/ButtonDropdownCombo";
import { Video, VideoOff } from "lucide-react";
import { Spinner } from "@platform/features/video/components/icons/Spinner";
import { DropdownButtonItem } from "@themes/pollinator/ui/DropdownButtonItem";

import { VideoTransformOptions } from "@core/features/video";
import { useMemoCompare } from "@core/features/video";
import { ContainerBreakpoints } from "../../videoMeeting.types";
import { videoEventBus } from "@core/features/video";
import { useBrowserCompatibilityFeatures } from "@platform/features/video/hooks/useBrowserCompatibilityFeatures";
import { usePlatformStore } from "@platform/store";

interface Props {
  /** The label that will be shown for video input control, it defaults to `Video`. */
  label?: string;
  /** The label that will be shown for the background blur button, it defaults to 'Enable Background Blur'. */
  backgroundBlurLabel?: string;
  containerBreakpoints: ContainerBreakpoints;
}

const VideoInputTransformControl: React.FC<Props> = ({
  label = "Video",
  backgroundBlurLabel = "Enable Background Blur",
  containerBreakpoints,
}) => {
  const meetingManager = useMeetingManager();
  const logger = useLogger();
  const { devices, selectedDevice } = useVideoInputs();
  const { isVideoEnabled, toggleVideo } = useLocalVideo();
  // Get browser compatibility features
  const { supportsVideo } = useBrowserCompatibilityFeatures();

  const { isBackgroundBlurSupported, createBackgroundBlurDevice } =
    useBackgroundBlur();
  const {
    isBackgroundReplacementSupported,
    createBackgroundReplacementDevice,
  } = useBackgroundReplacement();
  const [isLoading, setIsLoading] = useState(false);
  const [
    dropdownWithVideoTransformOptions,
    setDropdownWithVideoTransformOptions,
  ] = useState<ReactNode[] | null>(null);
  const [activeVideoTransformOption, setActiveVideoTransformOption] =
    useState<string>(VideoTransformOptions.None);
  const videoDevices = useMemoCompare(
    devices,
    (prev: typeof devices | undefined, next: typeof devices | undefined): boolean =>
      isEqual(prev, next)
  ) ?? [];

  const isBackgroundBlurEnabled = usePlatformStore(
    (s) => s.isBackgroundBlurEnabled
  );
  const setBackgroundBlurEnabled = usePlatformStore(
    (s) => s.setBackgroundBlurEnabled
  );

  useEffect(() => {
    if (isBackgroundBlurEnabled) {
      toggleBackgroundBlur();
      setActiveVideoTransformOption(VideoTransformOptions.Blur);
    } else {
      resetDeviceToIntrinsic();
    }
  }, []);

  // Reset the video input to intrinsic if current video input is a transform device because this component
  // does not know if blur or replacement was selected. This depends on how the demo is set up.
  // TODO: use a hook in the appState to track whether blur or replacement was selected before this component mounts,
  // or maintain the state of `activeVideoTransformOption` in `MeetingManager`.
  const resetDeviceToIntrinsic = async () => {
    try {
      if (isVideoTransformDevice(selectedDevice)) {
        const intrinsicDevice = await selectedDevice.intrinsicDevice();
        await meetingManager.selectVideoInputDevice(intrinsicDevice);
      }
    } catch (error) {
      logger.error("Failed to reset Device to intrinsic device");
    }
  };

  // Toggle background blur on/off.
  const toggleBackgroundBlur = async () => {
    let current = selectedDevice;
    if (isLoading || current === undefined) {
      return;
    }
    try {
      setIsLoading(true);

      if (!isVideoTransformDevice(current)) {
        // Enable video transform on the default device.
        current = (await createBackgroundBlurDevice(
          current
        )) as VideoTransformDevice;
      } else {
        // Switch back to intrinsicDevice.
        const intrinsicDevice = await current.intrinsicDevice();
        // Stop existing VideoTransformDevice.
        await current.stop();
        current = intrinsicDevice;
        // Switch to background blur device if old selection was background replacement otherwise switch to default intrinsic device.
        if (activeVideoTransformOption === VideoTransformOptions.Replacement) {
          current = (await createBackgroundBlurDevice(
            current
          )) as VideoTransformDevice;
        }
      }

      if (isVideoEnabled) {
        // Use the new created video device as input.
        await meetingManager.startVideoInputDevice(current);
      } else {
        // Select the new created video device but don't start it.
        await meetingManager.selectVideoInputDevice(current);
      }

      // Update the current selected transform.
      // Keep these as separate calls — nesting setBackgroundBlurEnabled inside
      // the setActiveVideoTransformOption updater causes a "setState during render" warning.
      setActiveVideoTransformOption((prev) =>
        prev === VideoTransformOptions.Blur
          ? VideoTransformOptions.None
          : VideoTransformOptions.Blur
      );
      setBackgroundBlurEnabled(
        activeVideoTransformOption !== VideoTransformOptions.Blur
      );
    } catch (e) {
      logger.error(`Error trying to toggle background blur ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleClick = async (deviceId: string): Promise<void> => {
      try {
        // If background blur/replacement is on, then re-use the same video transform pipeline, but replace the inner device
        // If background blur/replacement is not on, then do a normal video selection
        let newDevice: VideoInputDevice = deviceId;
        if (isVideoTransformDevice(selectedDevice) && !isLoading) {
          setIsLoading(true);
          if ("chooseNewInnerDevice" in selectedDevice) {
            // @ts-ignore
            newDevice = selectedDevice.chooseNewInnerDevice(deviceId);
          } else {
            logger.error("Transform device cannot choose new inner device");
            return;
          }
        }
        if (isVideoEnabled) {
          await meetingManager.startVideoInputDevice(newDevice);
        } else {
          meetingManager.selectVideoInputDevice(newDevice);
        }

        videoEventBus.emitEvent("videochat:video:source:selected", {
          deviceId,
        });
      } catch (error) {
        logger.error(
          "VideoInputTransformControl failed to select video input device"
        );
      } finally {
        setIsLoading(false);
      }
    };

    const getDropdownWithVideoTransformOptions = async (): Promise<void> => {
      const deviceOptions: ReactNode[] = await Promise.all(
        videoDevices.map(async (option) => (
          <DropdownButtonItem
            key={option.deviceId}
            checked={await isOptionActive(selectedDevice, option.deviceId)}
            onClick={async () => await handleClick(option.deviceId)}
          >
            <span>{option.label}</span>
          </DropdownButtonItem>
        ))
      );

      // Add 'Enable Background Blur' to the selection dropdown as an option if it's offered/supported.
      if (isBackgroundBlurSupported) {
        const videoTransformOptions: ReactNode = (
          <DropdownButtonItem
            key="backgroundBlurFilter"
            checked={activeVideoTransformOption === VideoTransformOptions.Blur}
            disabled={isLoading}
            onClick={toggleBackgroundBlur}
          >
            <>
              {isLoading && <Spinner width="1.5rem" height="1.5rem" />}
              {backgroundBlurLabel}
            </>
          </DropdownButtonItem>
        );
        deviceOptions.push(videoTransformOptions);
      }

      setDropdownWithVideoTransformOptions(deviceOptions);
    };

    getDropdownWithVideoTransformOptions();
  }, [
    createBackgroundBlurDevice,
    createBackgroundReplacementDevice,
    meetingManager,
    meetingManager.startVideoInputDevice,
    videoDevices,
    isLoading,
    isVideoEnabled,
    selectedDevice,
    isBackgroundBlurSupported,
    isBackgroundReplacementSupported,
  ]);

  const handleToggleVideo = async () => {
    try {
      if (isVideoEnabled) {
        videoEventBus.emitEvent("videochat:camera:off", {});
      } else {
        videoEventBus.emitEvent("videochat:camera:on", {});
      }
      await toggleVideo();
    } catch (error) {
      logger.error(`VideoInputTransformControl failed to toggle video: ${error}`);
    }
  };

  return (
    <ButtonDropdownCombo
      buttonIcon={isVideoEnabled ? Video : VideoOff}
      buttonLabel={
        containerBreakpoints && containerBreakpoints.isMd ? label : ""
      }
      buttonOnClick={supportsVideo ? handleToggleVideo : () => {}}
      dropdownOptions={supportsVideo ? (dropdownWithVideoTransformOptions || []) : []}
      destructive={!isVideoEnabled}
      disabled={!supportsVideo}
    />
  );
};

export default VideoInputTransformControl;
